import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, http } from '../client.ts';

/**
 * User admin hooks (ADMIN-only). The server's /api/users is paginated and
 * sends X-Total-Count; this hook pulls a single large page since the user
 * list is small. /api/users/authorities returns the master list of system
 * authority names ("ROLE_ADMIN", "ROLE_USER", "ROLE_GOVERNMENT_FORM", ...).
 */

export interface AdminUser {
  id: number;
  login: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  imageUrl: string | null;
  activated: boolean;
  langKey: string | null;
  authorities: string[];
}

export function useAdminUsers() {
  return useQuery({
    queryKey: ['admin-users'],
    queryFn: async (): Promise<AdminUser[]> => {
      const res = await api.get<AdminUser[]>('/api/users', { params: { size: 1000 } });
      if (res.status !== 200) throw new Error(`/api/users → ${res.status}`);
      return res.data;
    },
  });
}

export function useAdminUser(login: string | null | undefined) {
  return useQuery({
    queryKey: ['admin-user', login],
    queryFn: () => http.get<AdminUser>(`/api/users/${login}`),
    enabled: login != null && login !== '',
  });
}

export function useAuthorities() {
  return useQuery({
    queryKey: ['authorities'],
    queryFn: () => http.get<string[]>('/api/users/authorities'),
    staleTime: 5 * 60_000,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (u: Partial<AdminUser>) => http.post<AdminUser>('/api/users', u),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (u: AdminUser) => http.put<AdminUser>('/api/users', u),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      qc.invalidateQueries({ queryKey: ['admin-user'] });
    },
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (login: string) => http.delete(`/api/users/${login}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });
}
